import React, { useState } from 'react';
import { Button, Center, Group, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconArrowRight, IconBox, IconLayout, IconPlus } from '@tabler/icons-react';
import useSWR, { useSWRConfig } from 'swr';
import { notifications } from '@mantine/notifications';
import {
    createPlaylist,
    listComponents,
    listPlaylists,
    listScenes,
    updatePlaylist,
    type Playlist,
    type Scene,
} from '../../../data/ShellConfig';
import { listDeviceFetcher } from '../../../data/Device';
import { useCsrf } from '../../../hooks/useCsrf';
import { track } from '../../../utils/mixpanel';
import { AnalyticsEvents } from '../../../analytics/events';
import PlaylistCard from './PlaylistCard';
import AddSceneDrawer from './AddSceneDrawer';
import NewSceneDrawer from './NewSceneDrawer';
import LibraryDrawer, { type LibraryKind } from './LibraryDrawer';
import classes from './playlists.module.css';

type Picker = { mode: 'append'; playlistId: string } | { mode: 'create' } | null;

const DEFAULT_SCENE_DURATION = 60;

const LibraryEntry: React.FC<{
    icon: React.ReactNode;
    label: string;
    description: string;
    count: number;
    onClick: () => void;
}> = ({ icon, label, description, count, onClick }) => (
    <Paper
        withBorder
        radius="md"
        p="md"
        component="button"
        type="button"
        onClick={onClick}
        style={{ cursor: 'pointer', textAlign: 'left', display: 'block', width: '100%' }}
    >
        <Group justify="space-between" wrap="nowrap" mb={4}>
            <Group gap="xs">
                <ThemeIcon variant="light" color="gray" radius="md">
                    {icon}
                </ThemeIcon>
                <Text fw={600}>{label}</Text>
            </Group>
            <Text fw={600} c="dimmed">
                {count}
            </Text>
        </Group>
        <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text size="sm" c="dimmed">
                {description}
            </Text>
            <IconArrowRight size={18} color="var(--mantine-color-dimmed)" />
        </Group>
    </Paper>
);

const PlaylistsPage: React.FC = () => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();

    const { data: playlists, isLoading } = useSWR('api/playlists', listPlaylists);
    const { data: scenes = [] } = useSWR('api/scenes', listScenes);
    const { data: components = [] } = useSWR('api/components', listComponents);
    const { data: devices = [] } = useSWR('api/devices', listDeviceFetcher);

    // Maps a device's current playlist id to its name, so a card can warn before
    // moving a device off the playlist it is already assigned to.
    const playlistNameById = new Map((playlists ?? []).map(playlist => [playlist.id, playlist.name]));

    const [picker, setPicker] = useState<Picker>(null);
    const [editSceneId, setEditSceneId] = useState<string | null>(null);
    const [newSceneOpen, setNewSceneOpen] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [libraryKind, setLibraryKind] = useState<LibraryKind>('components');

    const openLibrary = (kind: LibraryKind) => {
        setLibraryKind(kind);
        setLibraryOpen(true);
    };

    const closePicker = () => setPicker(null);

    const handlePick = async (sceneId: string) => {
        if (!picker) return;
        try {
            if (picker.mode === 'create') {
                const created = await createPlaylist(
                    {
                        name: 'New playlist',
                        scenes: [{ sceneId, durationSeconds: DEFAULT_SCENE_DURATION }],
                    },
                    csrfToken
                );
                track(AnalyticsEvents.PlaylistCreated, { playlistId: created.id });
            } else {
                const target = (playlists ?? []).find(playlist => playlist.id === picker.playlistId);
                if (!target) return;
                const existing = (target.playlistScenes ?? []).map(entry => ({
                    sceneId: entry.sceneId,
                    durationSeconds: entry.durationSeconds,
                }));
                await updatePlaylist(
                    target.id,
                    {
                        name: target.name,
                        scenes: [...existing, { sceneId, durationSeconds: DEFAULT_SCENE_DURATION }],
                    },
                    csrfToken
                );
                track(AnalyticsEvents.PlaylistUpdated, {
                    source: 'playlist_editor',
                    action: 'add_scene',
                    playlistId: target.id,
                });
            }
            await mutate('api/playlists');
        } catch {
            notifications.show({ color: 'red', message: 'Could not add the scene.' });
        }
        closePicker();
    };

    // A freshly created scene is added to the playlist just like an existing one.
    const handleNewSceneSaved = async (scene: Scene) => {
        setNewSceneOpen(false);
        await mutate('api/scenes');
        await handlePick(scene.id);
    };

    const library = (
        <Stack className={classes.lib} gap="md">
            <div>
                <Title order={4}>Library</Title>
                <Text size="sm" c="dimmed">
                    Manage the components and scenes used to build playlists.
                </Text>
            </div>
            <LibraryEntry
                icon={<IconBox size={18} />}
                label="Components"
                description="Build components with PNG images or URLs"
                count={components.length}
                onClick={() => openLibrary('components')}
            />
            <LibraryEntry
                icon={<IconLayout size={18} />}
                label="Scenes"
                description="Lay components out into scene templates"
                count={scenes.length}
                onClick={() => openLibrary('scenes')}
            />
        </Stack>
    );

    return (
        <div className={classes.layout}>
            {library}

            <Stack className={classes.main} gap="lg">
                <Group justify="space-between" align="center">
                    <Title order={2}>Playlists</Title>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={() => setPicker({ mode: 'create' })}
                    >
                        New playlist
                    </Button>
                </Group>

                {isLoading ? (
                    <Center py="xl">
                        <Loader />
                    </Center>
                ) : (playlists ?? []).length === 0 ? (
                    <Paper withBorder radius="md" p="xl">
                        <Stack align="center" gap="sm">
                            <Text fw={600}>No playlists yet</Text>
                            <Text size="sm" c="dimmed" ta="center">
                                Create a playlist and add scenes to schedule content on your devices.
                            </Text>
                            <Button
                                mt="xs"
                                leftSection={<IconPlus size={16} />}
                                onClick={() => setPicker({ mode: 'create' })}
                            >
                                New playlist
                            </Button>
                        </Stack>
                    </Paper>
                ) : (
                    <Stack gap="lg">
                        {(playlists ?? []).map(playlist => (
                            <PlaylistCard
                                key={playlist.id}
                                playlist={playlist}
                                allDevices={devices}
                                playlistNameById={playlistNameById}
                                onAddScene={(target: Playlist) =>
                                    setPicker({ mode: 'append', playlistId: target.id })
                                }
                                onEditScene={setEditSceneId}
                                components={components}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            <AddSceneDrawer
                opened={picker !== null}
                onClose={closePicker}
                scenes={scenes}
                onPick={handlePick}
                onNewScene={() => setNewSceneOpen(true)}
                title={picker?.mode === 'create' ? 'New playlist — choose a first scene' : 'Add scene'}
            />

            <NewSceneDrawer
                opened={newSceneOpen}
                onClose={() => setNewSceneOpen(false)}
                components={components}
                onSaved={handleNewSceneSaved}
            />

            <NewSceneDrawer
                opened={editSceneId !== null}
                sceneId={editSceneId ?? undefined}
                onClose={() => setEditSceneId(null)}
                components={components}
                onSaved={() => {
                    // A scene edit ripples into the playlists (and the strip) that use it.
                    void Promise.all([mutate('api/scenes'), mutate('api/playlists')]);
                    setEditSceneId(null);
                }}
            />

            <LibraryDrawer
                kind={libraryKind}
                opened={libraryOpen}
                onClose={() => setLibraryOpen(false)}
            />
        </div>
    );
};

export default PlaylistsPage;
